/**
 * "Scan with phone": a QR code of `<this site>/scanner?tradeId=<id>`. Scanned on a phone signed in
 * to the same store, it opens the card scanner on this trade, and every card scanned there lands on
 * this page within seconds (useTradeAutoRefresh). The link is also shown as text, for a phone that
 * can't scan it. The code is drawn by the server (GET /trades/:id/pairing-qr), which already has a
 * QR encoder; the frontend has none.
 *
 * The trade is created first if this is still an unsaved session (the phone needs a real draft).
 */
import React, { useEffect, useState } from 'react'
import { Smartphone, X } from 'lucide-react'
import { api } from '../../../utils/api'
import { reportError } from '../../../utils/reportError'
import { NativeModalDialog } from './NativeModalDialog'
import styles from '../CreateTransaction.module.css'

type Pairing = { url: string; qrDataUrl: string }

export const PhonePairingModal: React.FC<{
  open: boolean
  onClose: () => void
  /** The draft's id, creating the draft if there isn't one yet. */
  ensureTradeId: () => Promise<string>
  /** The side the phone scans onto. */
  side?: 'store' | 'customer'
}> = ({ open, onClose, ensureTradeId, side = 'customer' }) => {
  const [pairing, setPairing] = useState<Pairing | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setPairing(null)
    setError(null)
    setCopied(false)
    void (async () => {
      try {
        const tradeId = await ensureTradeId()
        const res = await api.get(`/trades/${tradeId}/pairing-qr`, { params: { origin: window.location.origin, side } })
        const data = res.data?.data as Pairing | undefined
        if (!cancelled && data?.url && data.qrDataUrl) setPairing(data)
        else if (!cancelled) setError("Couldn't make the code. Try again.")
      } catch (err: unknown) {
        reportError(err, { page: 'CreateTransaction', action: 'phonePairingQr' })
        const anyErr = err as { response?: { data?: { message?: string; error?: string } } }
        if (!cancelled) setError(anyErr.response?.data?.message || anyErr.response?.data?.error || "Couldn't make the code. Try again.")
      }
    })()
    return () => { cancelled = true }
  }, [open, ensureTradeId, side])

  const copy = async () => {
    if (!pairing) return
    try {
      await navigator.clipboard.writeText(pairing.url)
      setCopied(true)
    } catch { /* the link is on screen to type */ }
  }

  return (
    <NativeModalDialog open={open} onClose={onClose} className={styles.nativeModalDialog}>
      <div className={`${styles.modal} ${styles.pairingModal}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderContent}>
            <Smartphone size={20} aria-hidden />
            <h3>Scan cards with a phone</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className={styles.modalContent}>
          <p className={styles.pairingHint}>
            Scan this code with a phone signed in to this store. The card scanner opens on this transaction: every card
            it scans appears here under {side === 'store' ? 'Store Items' : 'Customer Items'}.
          </p>
          {error && <p className={styles.pairingError} role="alert">{error}</p>}
          {!pairing && !error && <p className={styles.pairingHint} role="status">Making the code…</p>}
          {pairing && (
            <>
              <img className={styles.pairingQr} src={pairing.qrDataUrl} alt="QR code that opens the card scanner on this transaction" width={240} height={240} />
              <div className={styles.pairingUrlRow}>
                <code className={styles.pairingUrl}>{pairing.url}</code>
                <button type="button" className={styles.newTradeBtn} onClick={() => void copy()}>{copied ? 'Copied' : 'Copy link'}</button>
              </div>
            </>
          )}
        </div>
        <div className={styles.modalActions}>
          <button type="button" className={styles.cancelBtn} onClick={onClose}>Done</button>
        </div>
      </div>
    </NativeModalDialog>
  )
}
